import type { EventBus } from "../core/EventBus.ts";
import type { Grid } from "../core/Grid.ts";
import type { Pathfinding } from "../core/Pathfinding.ts";
import type { PathRegistry } from "../core/PathRegistry.ts";
import type { Vec2 } from "../core/types.ts";
import type { Tower } from "../entities/Tower.ts";
import type { Projectile } from "../entities/Projectile.ts";
import type { ObjectPool } from "../utils/ObjectPool.ts";
import type { WaveManager } from "../systems/WaveManager.ts";
import type { CombatSystem } from "../systems/CombatSystem.ts";
import type { EconomySystem } from "../systems/EconomySystem.ts";
import type { SynergySystem } from "../systems/SynergySystem.ts";
import type { DraftSystem } from "../systems/DraftSystem.ts";
import type { ScoreSystem } from "../systems/ScoreSystem.ts";
import type { EntityRenderer } from "../rendering/EntityRenderer.ts";
import type { GridRenderer } from "../rendering/GridRenderer.ts";
import type { ParticleSystem } from "../rendering/ParticleSystem.ts";
import type { ScreenEffects } from "../rendering/ScreenEffects.ts";
import type { DraftUI } from "../ui/DraftUI.ts";
import type { SkillOverview } from "../ui/SkillOverview.ts";
import type { TowerSelector } from "../ui/TowerSelector.ts";
import type { HUD } from "../ui/HUD.ts";
import type { WaveIndicator } from "../ui/WaveIndicator.ts";
import type { LiveBars } from "../ui/LiveBars.ts";
import type { SkillDeckBar } from "../ui/SkillDeckBar.ts";
import type { GameOverScreen } from "../ui/GameOverScreen.ts";
import type { VictoryScreen } from "../ui/VictoryScreen.ts";
import type { WebGL2Effects } from "../rendering/WebGL2Effects.ts";
import type { SellConfirmDialog } from "../ui/SellConfirmDialog.ts";
import type { BreachCinematic } from "../ui/BreachCinematic.ts";
import type { BreachSystem } from "../systems/BreachSystem.ts";
import type { SpawnDetectionSystem } from "../systems/SpawnDetectionSystem.ts";
import type { SpawnAlert } from "../ui/SpawnAlert.ts";
import type { EnemyReveal } from "../ui/EnemyReveal.ts";
import type { PathRevealAnimation } from "../rendering/PathRevealAnimation.ts";
import type { HazardPoolRenderer } from "../rendering/HazardPoolRenderer.ts";
import type { Element, TowerConfig } from "../entities/types.ts";
import type { Firewall } from "../entities/Firewall.ts";
import type { MetaState } from "../core/MetaState.ts";
import type { Rng } from "../core/Rng.ts";
import type { PayloadRack } from "../systems/PayloadRack.ts";
import type { PayloadDetonationFx } from "../rendering/PayloadDetonationFx.ts";
import type { PerfGovernor } from "../engine/PerfGovernor.ts";
import type { Card } from "../entities/types.ts";
import type { PayloadTargetMode } from "./cardEffectHandlers.ts";

/** Active logic-bomb charge planted on a cell (gs-level, ticked per frame). */
export interface LogicBombState {
  col: number;
  row: number;
  timer: number; // ms remaining until detonation
  baseDamage: number; // AoE damage, scaled by cluster size at detonation
}

export const PHASE_BUILD = "build" as const;
export const PHASE_COMBAT = "combat" as const;
export const PHASE_DRAFT = "draft" as const;
export const PHASE_GAME_OVER = "gameOver" as const;
export const PHASE_PAUSED = "paused" as const;
export const PHASE_CINEMATIC = "cinematic" as const;
export type GamePhase =
  | typeof PHASE_BUILD
  | typeof PHASE_COMBAT
  | typeof PHASE_DRAFT
  | typeof PHASE_GAME_OVER
  | typeof PHASE_PAUSED
  | typeof PHASE_CINEMATIC;

export type PlacementMode = "tower" | "firewall";

export interface RuntimeState {
  phase: GamePhase;
  gold: number;
  lives: number;
  wave: number;
  waveActive: boolean;
  selectedTowerType: Element | null;
  placementMode: PlacementMode;
  towers: Tower[];
  firewalls: Firewall[];
  maxFirewalls: number;
  pathPixels: Vec2[];
  timeWarpAvailable: boolean;
  timeWarpDuration: number;
  /** Recursive Loop skill: every Nth kill deals AoE */
  recursiveLoopThreshold: number;
  recursiveLoopCounter: number;
  /** Story choice flags */
  globalDamageMult: number;
  resonanceSynergyEnabled: boolean;
  bossHpMult: number;
  bossHealingDisabled: boolean;
  skillsCostZero: boolean;
  /** Card effects */
  firewallSlowActive: boolean;
  firewallSlowFactor: number;
  showAllHPBars: boolean;
  targetingMode: "default" | "fastest";
  previousPhase: GamePhase | null;
  /** Currently selected placed tower for range display (CR-11) */
  selectedTower: Tower | null;
  /** CR-28: Tower under mouse cursor for hover range indicator */
  hoveredTower: Tower | null;
  /** CR-40b: Run statistics */
  runStats: {
    enemiesKilled: number;
    towersBuilt: number;
    goldEarned: number;
    goldSpent: number;
  };
  /** CR-8: Wave countdown timer (seconds remaining, 0 = inactive) */
  waveCountdown: number;
  waveCountdownMax: number;
  /** CR-6 (B7): Disarm guard — prevents auto-start from re-firing after manual start */
  waveStartedThisCountdown: boolean;
  /** CR-41: Consecutive perfect waves (0 leaks) */
  perfectStreak: number;
  isEndlessMode: boolean;
  /** BL-70: Active logic-bomb charges (gs-level, ticked per frame; dormant = empty). */
  logicBombs: LogicBombState[];
  /** BL-70: Payload targeting state — non-null while aiming a targeted payload. */
  payloadTargetingMode: PayloadTargetMode | null;
  payloadTargetingCard: Card | null;
  /** BL-72: Max lives cap (for GLASS_PROTOCOL trade-off). */
  maxLives: number;
  /** BL-72: CASCADE card — synergy triggers splash AoE. */
  cascadeActive: boolean;
  cascadeDamage: number;
  /** Bug 16: the integrity (lives) bonus earned on the wave-35 campaign win,
   *  remembered so the endless-death game-over screen can show it (the live
   *  score is frozen on CONTINUE, so addLivesBonus would otherwise report 0). */
  campaignLivesBonus: number;
}

/** Shared context passed to all game subsystem setup functions. */
export interface GameContext {
  gs: RuntimeState;
  eventBus: EventBus;
  grid: Grid;
  pathfinding: Pathfinding;
  pathRegistry: PathRegistry;
  projectilePool: ObjectPool<Projectile>;
  economy: EconomySystem;
  waveManager: WaveManager;
  combatSystem: CombatSystem;
  synergySystem: SynergySystem;
  draftSystem: DraftSystem;
  scoreSystem: ScoreSystem;
  payloadRack: PayloadRack;
  entityRenderer: EntityRenderer;
  gridRenderer: GridRenderer;
  particleSystem: ParticleSystem;
  screenEffects: ScreenEffects;
  draftUI: DraftUI;
  skillOverview: SkillOverview;
  towerSelector: TowerSelector;
  hud: HUD;
  waveIndicator: WaveIndicator;
  /** BL-56b — the two live Pro-Bars (wave-progress + payload-charge). Optional
   *  so headless handler tests can build a partial context (like victoryScreen). */
  liveBars?: LiveBars;
  enemyReveal: EnemyReveal;
  skillDeckBar: SkillDeckBar;
  gameOverScreen: GameOverScreen;
  victoryScreen?: VictoryScreen;
  webglEffects: WebGL2Effects;
  /** BL-44 — the single perf load brain (arena-only auto-degrade ladder). */
  perfGovernor: PerfGovernor;
  sellDialog: SellConfirmDialog;
  breachSystem: BreachSystem;
  breachCinematic: BreachCinematic;
  towerData: Record<string, TowerConfig>;
  canvas: HTMLCanvasElement;
  ctx: CanvasRenderingContext2D;
  recalculatePath: () => void;
  failsafeAvailable: boolean;
  disposeHandlers?: () => void;
  firewallRenderer: { render(firewalls: Firewall[], time: number): void };
  hazardPoolRenderer: HazardPoolRenderer;
  gameBanner: {
    show(
      text: string,
      color?: string,
      duration?: number,
      priority?: number,
    ): boolean;
    showGlitch(
      text: string,
      color1: string,
      color2: string,
      duration?: number,
      priority?: number,
    ): void;
    update(dt: number): void;
    render(ctx: CanvasRenderingContext2D, width: number, height: number): void;
    reset(): void;
  };
  /** D-331 — the Intrusion Scanner's own multi-line reading surface (never
   *  GameBanner: a wave-preview list is data, not a message). */
  scannerReadout: {
    show(header: string, lines: string[]): void;
    dismiss(): void;
  };
  /** D-331 — the grid re-route's landing seam for STORY/NARRATION/PRAISE/
   *  STATUS text (wired to `CommsDirector.announceStatus` via the HUD). Only
   *  urgent gameplay warnings still call `gameBanner`; everything else that
   *  used to be a centred banner now calls this instead. Optional so headless
   *  handler tests can omit it (falls back to a silent no-op in
   *  `eventHandlers.ts`). */
  commsRouter?: {
    status(text: string, priority?: number): void;
  };
  floatingDmg: {
    spawn(x: number, y: number, amount: number, color?: string): void;
    /** S195/BL-281 — the big-hit/crit variant (⚡ amber 1.8×). */
    spawnCrit(x: number, y: number, amount: number): void;
    update(dt: number): void;
    draw(ctx: CanvasRenderingContext2D): void;
    clear(): void;
  };
  spawnAlert: SpawnAlert;
  pathRevealAnimation: PathRevealAnimation;
  payloadDetonationFx: PayloadDetonationFx;
  spawnDetectionSystem: SpawnDetectionSystem;
  metaState?: MetaState;
  /** Module-applied baselines for properties shared between modules and cards */
  moduleBaselines: {
    rangeMultiplier: number;
    bossDamageReduction: number;
    globalDamageMult: number;
  };
  rng: Rng;
}
